import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  FileQuestion,
  Plus,
  Trash2,
  GripVertical,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const questionTypes = [
  { value: 'single_choice', label: 'QCM - Réponse unique', hasChoices: true },
  { value: 'multiple_choice', label: 'QCM - Réponses multiples', hasChoices: true },
  { value: 'true_false', label: 'Vrai / Faux', hasChoices: true, fixedChoices: true },
  { value: 'short_answer', label: 'Réponse courte', hasChoices: false },
  { value: 'long_answer', label: 'Réponse longue / Essai', hasChoices: false },
  { value: 'matching', label: 'Appariement', hasMatching: true },
  { value: 'ordering', label: 'Classement / Ordre', hasOrdering: true },
];

export default function QuestionEditorPage() {
  const { workspaceId, questionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !questionId || questionId === 'new';
  
  // Build back URL with category query param
  const getBackUrl = (cat) => {
    const base = `/workspace/${workspaceId}/exams/questions`;
    const category = cat || formData.category;
    return category ? `${base}?cat=${encodeURIComponent(category)}` : base;
  };
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    type: 'single_choice',
    question_text: '',
    points: 1,
    difficulty: 'medium',
    category: '',
    tags: [],
    explanation: '',
    // For choice-based questions
    choices: [
      { id: crypto.randomUUID(), text: '', is_correct: false },
      { id: crypto.randomUUID(), text: '', is_correct: false },
    ],
    // For short/long answers
    correct_answer: '',
    model_answer: '',
    keywords: [],
    grading_rubric: '',
    // For matching
    matching_pairs: [
      { left: '', right: '' },
    ],
    // For ordering
    correct_order: [''],
  });
  
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  
  useEffect(() => {
    const init = async () => {
      if (workspaceId) {
        await loadCategories(workspaceId);
        
        if (!isNew) {
          await loadQuestion(workspaceId);
        } else {
          setLoading(false);
        }
      }
    };
    
    init();
  }, [workspaceId, questionId]);
  
  const loadQuestion = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions/${questionId}?workspace_id=${wsId}`);
      if (response.ok) {
        const question = await response.json();
        setFormData({
          type: question.type || 'single_choice',
          question_text: question.question_text || '',
          points: question.points || 1,
          difficulty: question.difficulty || 'medium',
          category: question.category || '',
          tags: question.tags || [],
          explanation: question.explanation || '',
          choices: question.choices || [
            { id: crypto.randomUUID(), text: '', is_correct: false },
            { id: crypto.randomUUID(), text: '', is_correct: false },
          ],
          correct_answer: question.correct_answer || '',
          model_answer: question.model_answer || '',
          keywords: question.keywords || [],
          grading_rubric: question.grading_rubric || '',
          matching_pairs: question.matching_pairs || [{ left: '', right: '' }],
          correct_order: question.correct_order || [''],
        });
      } else {
        toast.error('Question non trouvée');
        navigate(getBackUrl(''));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate(getBackUrl(''));
    } finally {
      setLoading(false);
    }
  };
  
  const loadCategories = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/categories?workspace_id=${wsId}`);
      const cats = await response.json();
      setCategories(cats || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };
  
  const handleSave = async () => {
    if (!formData.question_text.trim()) {
      toast.error('L\'intitulé de la question est requis');
      return;
    }
    
    // Validate based on type
    const typeConfig = questionTypes.find((t) => t.value === formData.type);
    
    if (typeConfig?.hasChoices) {
      const validChoices = formData.choices.filter((c) => c.text.trim());
      if (validChoices.length < 2) {
        toast.error('Au moins 2 choix sont requis');
        return;
      }
      const hasCorrect = validChoices.some((c) => c.is_correct);
      if (!hasCorrect) {
        toast.error('Au moins une réponse correcte est requise');
        return;
      }
    }
    
    setSaving(true);
    try {
      const url = isNew 
        ? `${API_URL}/api/exams/questions?workspace_id=${workspaceId}`
        : `${API_URL}/api/exams/questions/${questionId}?workspace_id=${workspaceId}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      // Clean data based on type
      const dataToSend = {
        type: formData.type,
        question_text: formData.question_text,
        points: formData.points,
        difficulty: formData.difficulty,
        category: formData.category || null,
        tags: formData.tags,
        explanation: formData.explanation || null,
      };
      
      if (typeConfig?.hasChoices) {
        dataToSend.choices = formData.choices.filter((c) => c.text.trim());
      }
      
      if (formData.type === 'short_answer' || formData.type === 'long_answer') {
        dataToSend.correct_answer = formData.correct_answer || null;
        dataToSend.model_answer = formData.model_answer || null;
        dataToSend.keywords = formData.keywords;
        dataToSend.grading_rubric = formData.grading_rubric || null;
      }
      
      if (formData.type === 'matching') {
        dataToSend.matching_pairs = formData.matching_pairs.filter((p) => p.left.trim() && p.right.trim());
      }
      
      if (formData.type === 'ordering') {
        dataToSend.correct_order = formData.correct_order.filter((o) => o.trim());
      }
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(isNew ? 'Question créée' : 'Question mise à jour');
        
        if (isNew) {
          navigate(`/workspace/${workspaceId}/exams/questions/${result.id}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTypeChange = (newType) => {
    setFormData((prev) => {
      const updated = { ...prev, type: newType };
      
      // Reset choices for true/false
      if (newType === 'true_false') {
        updated.choices = [
          { id: crypto.randomUUID(), text: 'Vrai', is_correct: false },
          { id: crypto.randomUUID(), text: 'Faux', is_correct: false },
        ];
      } else if ((newType === 'single_choice' || newType === 'multiple_choice') && prev.type === 'true_false') {
        updated.choices = [
          { id: crypto.randomUUID(), text: '', is_correct: false },
          { id: crypto.randomUUID(), text: '', is_correct: false },
        ];
      }
      
      return updated;
    });
  };
  
  const addChoice = () => {
    setFormData((prev) => ({
      ...prev,
      choices: [...prev.choices, { id: crypto.randomUUID(), text: '', is_correct: false }],
    }));
  };
  
  const removeChoice = (id) => {
    setFormData((prev) => ({
      ...prev,
      choices: prev.choices.filter((c) => c.id !== id),
    }));
  };
  
  const updateChoice = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      choices: prev.choices.map((c) => {
        if (c.id === id) {
          // For single choice, unselect others when selecting one
          if (field === 'is_correct' && value && formData.type === 'single_choice') {
            return { ...c, is_correct: true };
          }
          return { ...c, [field]: value };
        }
        // Unselect others for single choice
        if (field === 'is_correct' && value && formData.type === 'single_choice') {
          return { ...c, is_correct: false };
        }
        return c;
      }),
    }));
  };
  
  const addMatchingPair = () => {
    setFormData((prev) => ({
      ...prev,
      matching_pairs: [...prev.matching_pairs, { left: '', right: '' }],
    }));
  };
  
  const removeMatchingPair = (index) => {
    setFormData((prev) => ({
      ...prev,
      matching_pairs: prev.matching_pairs.filter((_, i) => i !== index),
    }));
  };
  
  const updateMatchingPair = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      matching_pairs: prev.matching_pairs.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      ),
    }));
  };
  
  const addOrderItem = () => {
    setFormData((prev) => ({
      ...prev,
      correct_order: [...prev.correct_order, ''],
    }));
  };
  
  const removeOrderItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      correct_order: prev.correct_order.filter((_, i) => i !== index),
    }));
  };
  
  const updateOrderItem = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      correct_order: prev.correct_order.map((o, i) => i === index ? value : o),
    }));
  };
  
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };
  
  const removeTag = (tag) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };
  
  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData((prev) => ({ ...prev, keywords: [...prev.keywords, keywordInput.trim()] }));
      setKeywordInput('');
    }
  };
  
  const removeKeyword = (kw) => {
    setFormData((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== kw) }));
  };
  
  const typeConfig = questionTypes.find((t) => t.value === formData.type);
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  return (
    <WorkspaceLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackUrl())}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileQuestion className="h-6 w-6" />
                {isNew ? 'Nouvelle question' : 'Modifier la question'}
              </h1>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(getBackUrl())}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        
        {/* Main Form */}
        <div className="grid gap-6">
          {/* Question Type */}
          <Card>
            <CardHeader>
              <CardTitle>Type de question</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={formData.type} onValueChange={handleTypeChange}>
                <SelectTrigger className="w-full md:w-[300px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {questionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
          
          {/* Question Content */}
          <Card>
            <CardHeader>
              <CardTitle>Contenu de la question</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Intitulé de la question *</Label>
                <RichTextEditor
                  content={formData.question_text}
                  onChange={(html) => setFormData({ ...formData, question_text: html })}
                  placeholder="Écrivez votre question ici..."
                />
              </div>
              
              {/* Choices for QCM and True/False */}
              {typeConfig?.hasChoices && (
                <div className="space-y-3">
                  <Label>Choix de réponses</Label>
                  {formData.choices.map((choice, index) => (
                    <div key={choice.id} className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center cursor-pointer transition-colors ${
                          choice.is_correct 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-gray-300 hover:border-green-400'
                        }`}
                        onClick={() => updateChoice(choice.id, 'is_correct', !choice.is_correct)}
                      >
                        {choice.is_correct && <CheckCircle className="h-5 w-5" />}
                      </div>
                      <Input
                        value={choice.text}
                        onChange={(e) => updateChoice(choice.id, 'text', e.target.value)}
                        placeholder={`Choix ${index + 1}`}
                        disabled={typeConfig?.fixedChoices}
                        className="flex-1"
                      />
                      {!typeConfig?.fixedChoices && formData.choices.length > 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeChoice(choice.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!typeConfig?.fixedChoices && (
                    <Button variant="outline" size="sm" onClick={addChoice}>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter un choix
                    </Button>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Cliquez sur le cercle pour marquer la/les bonne(s) réponse(s)
                  </p>
                </div>
              )}
              
              {/* Short/Long Answer */}
              {(formData.type === 'short_answer' || formData.type === 'long_answer') && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Réponse attendue / Modèle</Label>
                    {formData.type === 'long_answer' ? (
                      <RichTextEditor
                        content={formData.model_answer}
                        onChange={(html) => setFormData({ ...formData, model_answer: html })}
                        placeholder="La réponse modèle ou attendue..."
                      />
                    ) : (
                      <Textarea
                        value={formData.model_answer}
                        onChange={(e) => setFormData({ ...formData, model_answer: e.target.value })}
                        placeholder="La réponse modèle ou attendue..."
                        rows={2}
                      />
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Mots-clés pour la correction</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.keywords.map((kw) => (
                        <Badge key={kw} variant="secondary" className="cursor-pointer" onClick={() => removeKeyword(kw)}>
                          {kw} ×
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        placeholder="Ajouter un mot-clé..."
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      />
                      <Button variant="outline" onClick={addKeyword}>Ajouter</Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Barème de notation (pour l'IA)</Label>
                    <Textarea
                      value={formData.grading_rubric}
                      onChange={(e) => setFormData({ ...formData, grading_rubric: e.target.value })}
                      placeholder="Ex: 2pts pour définition, 2pts pour exemples..."
                      rows={2}
                    />
                  </div>
                </div>
              )}
              
              {/* Matching */}
              {formData.type === 'matching' && (
                <div className="space-y-3">
                  <Label>Paires à associer</Label>
                  {formData.matching_pairs.map((pair, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        value={pair.left}
                        onChange={(e) => updateMatchingPair(index, 'left', e.target.value)}
                        placeholder="Élément gauche"
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">→</span>
                      <Input
                        value={pair.right}
                        onChange={(e) => updateMatchingPair(index, 'right', e.target.value)}
                        placeholder="Correspondance"
                        className="flex-1"
                      />
                      {formData.matching_pairs.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeMatchingPair(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addMatchingPair}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une paire
                  </Button>
                </div>
              )}
              
              {/* Ordering */}
              {formData.type === 'ordering' && (
                <div className="space-y-3">
                  <Label>Éléments à ordonner (dans l'ordre correct)</Label>
                  {formData.correct_order.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <Input
                        value={item}
                        onChange={(e) => updateOrderItem(index, e.target.value)}
                        placeholder={`Élément ${index + 1}`}
                        className="flex-1"
                      />
                      {formData.correct_order.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOrderItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addOrderItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un élément
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Paramètres</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Points</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.points}
                    onChange={(e) => setFormData({ ...formData, points: parseFloat(e.target.value) || 1 })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Difficulté</Label>
                  <Select
                    value={formData.difficulty}
                    onValueChange={(v) => setFormData({ ...formData, difficulty: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Facile</SelectItem>
                      <SelectItem value="medium">Moyen</SelectItem>
                      <SelectItem value="hard">Difficile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select
                    value={formData.category || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, category: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id || cat.name || cat} value={cat.name || cat}>{cat.name || cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Ajouter un tag..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button variant="outline" onClick={addTag}>Ajouter</Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Explication / Feedback</Label>
                <RichTextEditor
                  content={formData.explanation}
                  onChange={(html) => setFormData({ ...formData, explanation: html })}
                  placeholder="Explication affichée après la correction..."
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
